import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Heart, MessageCircle, Mic, Square, Volume2, User as UserIcon, Mail, Trash2, Eye, TreePine, ChevronDown, Settings, LogOut, Edit, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import WordCloudDisplay from '@/components/WordCloudDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MemorialTributePage({ user }) {
  const { memorialId } = useParams();
  const navigate = useNavigate();
  const [memorial, setMemorial] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTribute, setShowAddTribute] = useState(false);
  const [showWordCloud, setShowWordCloud] = useState(false);
  const [showEditMemorial, setShowEditMemorial] = useState(false);
  const [loginOption, setLoginOption] = useState('guest');
  const [contributorName, setContributorName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMessageId, setRecordingMessageId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [tributeType, setTributeType] = useState('text'); // 'text' or 'voice'
  const [audioBlob, setAudioBlob] = useState(null);
  const [editForm, setEditForm] = useState({});
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const isHost = memorial?.host_user_id && currentUser?.user_id === memorial.host_user_id;

  useEffect(() => {
    fetchData();
    // Use passed user prop first, then verify with backend
    if (user) {
      setCurrentUser(user);
    } else {
      checkAuth();
    }
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [memorialId, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setCurrentUser(response.data);
    } catch (error) {
      // User not authenticated - that's fine for public page
      setCurrentUser(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const openEditModal = () => {
    if (memorial) {
      setEditForm({
        deceased_name: memorial.deceased_name || '',
        deceased_photo: memorial.deceased_photo || '',
        birth_date: memorial.birth_date || '',
        death_date: memorial.death_date || '',
        service_date: memorial.service_date || '',
        service_type: memorial.service_type || '',
        host_message: memorial.host_message || ''
      });
      setShowEditMemorial(true);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.put(`${API}/memorial/${memorialId}`, editForm, {
        withCredentials: true
      });
      setMemorial(response.data);
      setShowEditMemorial(false);
      toast.success('Memorial updated successfully');
    } catch (error) {
      console.error('Error updating memorial:', error);
      toast.error(error.response?.data?.detail || 'Failed to update memorial');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchData = async () => {
    try {
      const [memorialRes, messagesRes] = await Promise.all([
        axios.get(`${API}/memorial/${memorialId}`),
        axios.get(`${API}/memorial/${memorialId}/messages`)
      ]);
      setMemorial(memorialRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load memorial');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/memorial/${memorialId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contributorName.trim() && !isAnonymous) {
      toast.error('Please enter your name or choose anonymous');
      return;
    }

    if (tributeType === 'text' && !messageText.trim()) {
      toast.error('Please add a message');
      return;
    }

    if (tributeType === 'voice' && !audioBlob) {
      toast.error('Please record a voice message');
      return;
    }

    setSubmitting(true);

    try {
      let newMessage;
      if (tributeType === 'voice' && audioBlob) {
        // First create the message, then upload audio
        const response = await axios.post(`${API}/memorial/messages`, {
          memorial_id: memorialId,
          contributor_name: isAnonymous ? 'Anonymous' : contributorName,
          is_anonymous: isAnonymous,
          message_text: '[Voice Tribute]'
        });
        newMessage = response.data;

        // Upload audio
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');
        const audioResponse = await axios.post(`${API}/memorial/${memorialId}/audio/${response.data.message_id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newMessage.audio_url = audioResponse.data.audio_url;
      } else {
        const response = await axios.post(`${API}/memorial/messages`, {
          memorial_id: memorialId,
          contributor_name: isAnonymous ? 'Anonymous' : contributorName,
          is_anonymous: isAnonymous,
          message_text: messageText
        });
        newMessage = response.data;
      }

      // Add the new message to state immediately for instant UI update
      setMessages(prevMessages => [newMessage, ...prevMessages]);
      
      toast.success('Your tribute has been added');
      setContributorName('');
      setMessageText('');
      setIsAnonymous(false);
      setShowAddTribute(false);
      setAudioBlob(null);
      setTributeType('text');
    } catch (error) {
      console.error('Error submitting tribute:', error);
      toast.error('Failed to submit tribute');
    } finally {
      setSubmitting(false);
    }
  };

  const startRecording = async (messageId) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        try {
          const formData = new FormData();
          formData.append('audio', audioBlob, 'recording.webm');
          await axios.post(`${API}/memorial/${memorialId}/audio/${messageId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Voice message added');
          fetchMessages();
        } catch (error) {
          toast.error('Failed to upload voice message');
        }
        
        setIsRecording(false);
        setRecordingMessageId(null);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingMessageId(messageId);
      toast.success('Recording started');
    } catch (error) {
      toast.error('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      toast.info('Processing recording...');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await axios.delete(`${API}/memorial/${memorialId}/messages/${messageId}`, {
        withCredentials: true
      });
      toast.success('Message deleted');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const audioMessages = messages.filter(m => m.audio_url);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!memorial) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif text-foreground mb-4">Memorial Not Found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          
          {/* Center Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Button 
              variant="ghost"
              onClick={() => navigate('/memorial-tribute')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Memorial Tribute
            </Button>
            <Button 
              variant="ghost"
              onClick={() => navigate('/family-tree')}
              className="text-foreground-muted hover:text-primary hover:bg-primary/5 rounded-full px-5 py-2 font-sans text-sm"
            >
              <TreePine className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Family Tree
            </Button>
          </div>

          {/* User Menu or Sign In */}
          {currentUser ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {currentUser.picture ? (
                  <img src={currentUser.picture} alt={currentUser.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserIcon className="w-4 h-4 text-primary" />
                  </div>
                )}
                <span className="text-sm font-sans text-foreground hidden sm:block">{currentUser.name}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-border rounded-lg shadow-lg py-1 z-50">
                  <button
                    onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                    className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-gray-50 flex items-center"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Account Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Button 
              onClick={() => {
                // Store current tribute page path for redirect after auth
                const currentPath = window.location.pathname;
                sessionStorage.setItem('authRedirectPath', currentPath);
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/home')}`;
              }}
              data-testid="tribute-sign-in-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-sans text-sm"
            >
              Sign In
            </Button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-primary/10 to-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Photo */}
            <div className="flex-shrink-0 relative">
              <img
                src={memorial.deceased_photo}
                alt={memorial.deceased_name}
                className="w-48 h-48 md:w-64 md:h-64 rounded-lg border-4 border-white shadow-floating object-cover"
                data-testid="memorial-photo"
              />
              {isHost && (
                <button
                  onClick={openEditModal}
                  className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-primary p-2 rounded-full shadow-md transition-all"
                  data-testid="edit-photo-btn"
                  title="Edit Memorial"
                >
                  <Edit className="w-4 h-4" strokeWidth={1.5} />
                </button>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <Heart className="w-12 h-12 text-primary" strokeWidth={1.5} />
                {isHost && (
                  <Button
                    onClick={openEditModal}
                    variant="outline"
                    size="sm"
                    data-testid="edit-memorial-btn"
                    className="border border-primary/30 text-primary hover:bg-primary/5 rounded-full px-4 py-1 text-xs"
                  >
                    <Edit className="w-3 h-3 mr-1" strokeWidth={1.5} />
                    Edit Memorial
                  </Button>
                )}
              </div>
              <h1 className="text-5xl md:text-6xl font-serif tracking-tight text-foreground mb-3" data-testid="deceased-name">
                {memorial.deceased_name}
              </h1>
              {(memorial.birth_date || memorial.death_date) && (
                <p className="text-xl font-serif text-foreground-muted mb-4">
                  {memorial.birth_date} {memorial.death_date && `- ${memorial.death_date}`}
                </p>
              )}
              {memorial.service_date && memorial.service_type && (
                <div className="inline-block bg-white border border-border rounded-lg px-6 py-3 mb-4">
                  <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">{memorial.service_type}</p>
                  <p className="text-base font-sans text-foreground">{memorial.service_date}</p>
                </div>
              )}
              {memorial.host_message && (
                <p className="text-lg font-sans text-foreground-muted leading-relaxed max-w-2xl" data-testid="host-message">
                  {memorial.host_message}
                </p>
              )}
              
              {/* Share on WhatsApp */}
              <div className="mt-6">
                <Button
                  onClick={() => {
                    const shareText = `Please add your tributes for '${memorial.deceased_name}' here: ${window.location.href}`;
                    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  data-testid="share-whatsapp-btn"
                  className="bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-2 font-sans text-sm shadow-md"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Share on WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-serif text-foreground">Tributes ({messages.length})</h2>
          <div className="flex space-x-3">
            {isHost && (
              <Button
                onClick={() => setShowWordCloud(!showWordCloud)}
                data-testid="toggle-wordcloud-btn"
                variant="outline"
                className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
              >
                <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {showWordCloud ? 'Hide' : 'View'} Word Cloud
              </Button>
            )}
            <Button
              onClick={() => setShowAddTribute(true)}
              data-testid="add-tribute-btn"
              className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-6 py-2 font-serif"
            >
              <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Add Tribute
            </Button>
          </div>
        </div>

        {showWordCloud && isHost && (
          <div className="bg-white border border-border shadow-card rounded-lg p-6 mb-6">
            <h3 className="text-xl font-serif text-foreground mb-4">Words That Describe Them</h3>
            <WordCloudDisplay memorialId={memorialId} />
          </div>
        )}

        {/* Tributes Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Tributes (2/3) */}
          <div className="lg:col-span-2 space-y-4">
            {messages.length === 0 ? (
              <div className="bg-white border border-border shadow-card rounded-lg p-12 text-center">
                <Heart className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-base font-sans text-foreground-muted">Be the first to share a tribute</p>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  key={message.message_id}
                  className="bg-white border border-border shadow-card rounded-lg p-6 fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`tribute-card-${index}`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="text-lg font-serif text-foreground" data-testid="contributor-name">
                      {message.contributor_name}
                      {message.is_anonymous && <span className="text-sm text-muted-foreground ml-2">(Anonymous)</span>}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
                        {new Date(message.created_at).toLocaleDateString()}
                      </span>
                      {isHost && (
                        <button
                          onClick={() => handleDelete(message.message_id)}
                          className="text-status-error hover:text-status-error/80"
                          data-testid="delete-message-btn"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  </div>

                  {message.message_text && (
                    <p className="text-base font-sans text-foreground-muted leading-relaxed mb-4 whitespace-pre-wrap">
                      {message.message_text}
                    </p>
                  )}

                  {!message.audio_url && (
                    <div className="pt-3 border-t border-border">
                      {isRecording && recordingMessageId === message.message_id ? (
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-2 text-status-error">
                            <div className="w-3 h-3 bg-status-error rounded-full animate-pulse"></div>
                            <span className="text-sm font-sans">Recording...</span>
                          </div>
                          <Button
                            onClick={stopRecording}
                            size="sm"
                            variant="outline"
                            className="border border-status-error text-status-error hover:bg-status-error/5 rounded-full"
                          >
                            <Square className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Stop & Save
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => startRecording(message.message_id)}
                          size="sm"
                          variant="outline"
                          className="border border-primary/20 text-primary hover:bg-primary/5 rounded-full"
                        >
                          <Mic className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          Add Voice Message
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Audio Tributes Sidebar (1/3) */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-border shadow-card rounded-lg p-6 sticky top-6">
              <h3 className="text-xl font-serif text-foreground mb-4 flex items-center">
                <Volume2 className="w-5 h-5 mr-2 text-primary" strokeWidth={1.5} />
                Voice Tributes ({audioMessages.length})
              </h3>

              {audioMessages.length === 0 ? (
                <p className="text-sm font-sans text-foreground-muted italic">
                  No voice tributes yet. Be the first to add one!
                </p>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {audioMessages.map((message) => (
                    <div key={message.message_id} className="p-4 bg-paper rounded-lg">
                      <p className="text-sm font-serif text-foreground mb-2">{message.contributor_name}</p>
                      <audio controls src={`${BACKEND_URL}${message.audio_url}`} className="w-full" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Tribute Modal */}
      {showAddTribute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddTribute(false)}>
          <div className="bg-white rounded-lg shadow-floating max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-paper border-b border-border p-6">
              <h2 className="text-2xl font-serif text-foreground">Add Your Tribute</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Name Section */}
              <div>
                <label className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-sans text-foreground">Post anonymously</span>
                </label>

                {!isAnonymous && (
                  <Input
                    placeholder="Your Name"
                    value={contributorName}
                    onChange={(e) => setContributorName(e.target.value)}
                    data-testid="tribute-name-input"
                    className="bg-transparent border-b-2 border-border focus:border-primary rounded-none px-0 py-3 font-sans"
                    required={!isAnonymous}
                  />
                )}
              </div>

              {/* Tribute Type Selector */}
              <div>
                <p className="text-sm font-sans text-foreground-muted mb-3">Choose tribute type:</p>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setTributeType('text')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-sans text-sm transition-all ${
                      tributeType === 'text'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-white text-foreground border-border hover:border-primary'
                    }`}
                  >
                    <MessageCircle className="w-5 h-5 mx-auto mb-1" strokeWidth={1.5} />
                    Text Message
                  </button>
                  <button
                    type="button"
                    onClick={() => setTributeType('voice')}
                    className={`flex-1 px-4 py-3 rounded-lg border-2 font-sans text-sm transition-all ${
                      tributeType === 'voice'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-white text-foreground border-border hover:border-primary'
                    }`}
                  >
                    <Mic className="w-5 h-5 mx-auto mb-1" strokeWidth={1.5} />
                    Voice Recording
                  </button>
                </div>
              </div>

              {/* Text Tribute */}
              {tributeType === 'text' && (
                <div>
                  <Textarea
                    placeholder="Share your memories, thoughts, and feelings..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    data-testid="tribute-message-input"
                    className="bg-transparent border border-border focus:border-primary rounded-lg p-4 font-sans min-h-[120px]"
                    required={tributeType === 'text'}
                  />
                </div>
              )}

              {/* Voice Tribute */}
              {tributeType === 'voice' && (
                <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
                  {!isRecording && !audioBlob && (
                    <div>
                      <Mic className="w-16 h-16 text-primary mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-foreground-muted mb-4">Click to start recording your voice tribute</p>
                      <Button
                        type="button"
                        onClick={async () => {
                          try {
                            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                            const mediaRecorder = new MediaRecorder(stream);
                            mediaRecorderRef.current = mediaRecorder;
                            chunksRef.current = [];

                            mediaRecorder.ondataavailable = (e) => {
                              if (e.data.size > 0) chunksRef.current.push(e.data);
                            };

                            mediaRecorder.onstop = () => {
                              const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                              setAudioBlob(blob);
                              stream.getTracks().forEach(track => track.stop());
                              setIsRecording(false);
                            };

                            mediaRecorder.start();
                            setIsRecording(true);
                            toast.success('Recording started');
                          } catch (error) {
                            toast.error('Failed to access microphone. Please allow microphone access.');
                          }
                        }}
                        className="bg-red-500 text-white hover:bg-red-600 rounded-full px-8 py-3"
                      >
                        <Mic className="w-5 h-5 mr-2" />
                        Start Recording
                      </Button>
                    </div>
                  )}

                  {isRecording && (
                    <div>
                      <div className="w-20 h-20 bg-red-500 rounded-full mx-auto mb-4 animate-pulse flex items-center justify-center">
                        <Mic className="w-10 h-10 text-white" strokeWidth={1.5} />
                      </div>
                      <p className="text-foreground mb-4">Recording in progress...</p>
                      <Button
                        type="button"
                        onClick={() => {
                          if (mediaRecorderRef.current) {
                            mediaRecorderRef.current.stop();
                          }
                        }}
                        className="bg-gray-700 text-white hover:bg-gray-800 rounded-full px-8 py-3"
                      >
                        <Square className="w-5 h-5 mr-2" />
                        Stop Recording
                      </Button>
                    </div>
                  )}

                  {audioBlob && !isRecording && (
                    <div>
                      <Volume2 className="w-16 h-16 text-green-500 mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-foreground mb-4">Recording complete!</p>
                      <audio controls src={URL.createObjectURL(audioBlob)} className="mx-auto mb-4" />
                      <div className="flex justify-center space-x-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setAudioBlob(null)}
                          className="border-2 border-gray-300 text-gray-600 hover:bg-gray-100 rounded-full px-6 py-2"
                        >
                          Re-record
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddTribute(false);
                    setAudioBlob(null);
                    setTributeType('text');
                  }}
                  className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || (tributeType === 'voice' && !audioBlob)}
                  data-testid="submit-tribute-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif"
                >
                  {submitting ? 'Submitting...' : 'Add Tribute'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Memorial Modal */}
      {showEditMemorial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEditMemorial(false)}>
          <div className="bg-white rounded-lg shadow-floating max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-paper border-b border-border p-6">
              <h2 className="text-2xl font-serif text-foreground">Edit Memorial</h2>
              <p className="text-sm text-foreground-muted mt-1">Update the memorial details</p>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-sans text-foreground mb-2">Name *</label>
                <Input
                  placeholder="Name of your loved one"
                  value={editForm.deceased_name || ''}
                  onChange={(e) => setEditForm({...editForm, deceased_name: e.target.value})}
                  data-testid="edit-name-input"
                  className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-3 font-sans"
                  required
                />
              </div>

              {/* Photo URL */}
              <div>
                <label className="block text-sm font-sans text-foreground mb-2">Photo URL *</label>
                <Input
                  placeholder="URL to photo"
                  value={editForm.deceased_photo || ''}
                  onChange={(e) => setEditForm({...editForm, deceased_photo: e.target.value})}
                  data-testid="edit-photo-input"
                  className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-3 font-sans"
                  required
                />
                {editForm.deceased_photo && (
                  <div className="mt-2">
                    <img src={editForm.deceased_photo} alt="Preview" className="w-24 h-24 object-cover rounded-lg border" />
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-sans text-foreground mb-2">Birth Date</label>
                  <Input
                    placeholder="e.g., January 1, 1940"
                    value={editForm.birth_date || ''}
                    onChange={(e) => setEditForm({...editForm, birth_date: e.target.value})}
                    data-testid="edit-birth-date-input"
                    className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-3 font-sans"
                  />
                </div>
                <div>
                  <label className="block text-sm font-sans text-foreground mb-2">Death Date</label>
                  <Input
                    placeholder="e.g., December 31, 2024"
                    value={editForm.death_date || ''}
                    onChange={(e) => setEditForm({...editForm, death_date: e.target.value})}
                    data-testid="edit-death-date-input"
                    className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-3 font-sans"
                  />
                </div>
              </div>

              {/* Service Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-sans text-foreground mb-2">Service Type</label>
                  <select
                    value={editForm.service_type || ''}
                    onChange={(e) => setEditForm({...editForm, service_type: e.target.value})}
                    data-testid="edit-service-type-select"
                    className="w-full bg-transparent border border-border focus:border-primary rounded-lg px-4 py-3 font-sans"
                  >
                    <option value="">Select service type</option>
                    <option value="Memorial Service">Memorial Service</option>
                    <option value="Bhog and Ardas">Bhog and Ardas</option>
                    <option value="Tehrvi">Tehrvi</option>
                    <option value="Funeral Service">Funeral Service</option>
                    <option value="Celebration of Life">Celebration of Life</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-sans text-foreground mb-2">Service Date</label>
                  <Input
                    placeholder="e.g., January 15, 2025"
                    value={editForm.service_date || ''}
                    onChange={(e) => setEditForm({...editForm, service_date: e.target.value})}
                    data-testid="edit-service-date-input"
                    className="bg-transparent border border-border focus:border-primary rounded-lg px-4 py-3 font-sans"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-sans text-foreground mb-2">Memorial Message</label>
                <Textarea
                  placeholder="A message from you about your loved one..."
                  value={editForm.host_message || ''}
                  onChange={(e) => setEditForm({...editForm, host_message: e.target.value})}
                  data-testid="edit-message-input"
                  className="bg-transparent border border-border focus:border-primary rounded-lg p-4 font-sans min-h-[100px]"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditMemorial(false)}
                  className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-6 py-2 font-serif"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="save-memorial-btn"
                  className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-full px-8 py-3 font-serif"
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
